/**
 * Table data extraction and formatting utilities.
 * Adapted from streamdown's internal table/utils.ts
 */

type TableData = {
  headers: string[];
  rows: string[][];
};

/**
 * Extracts structured data from an HTML table element.
 */
export const parseTableData = (table: HTMLTableElement): TableData => {
  const headers: string[] = [];
  const rows: string[][] = [];

  for (const th of table.querySelectorAll('thead th')) {
    headers.push(th.textContent?.trim() || '');
  }

  for (const tr of table.querySelectorAll('tbody tr')) {
    const row: string[] = [];
    for (const td of tr.querySelectorAll('td')) {
      row.push(td.textContent?.trim() || '');
    }
    rows.push(row);
  }

  return { headers, rows };
};

/**
 * Formats table data as CSV with proper quoting/escaping.
 */
export const toCsv = ({ headers, rows }: TableData): string => {
  const escapeCell = (cell: string): string => {
    let needsQuotes = false;
    let hasDoubleQuote = false;

    for (const ch of cell) {
      if (ch === '"') {
        needsQuotes = true;
        hasDoubleQuote = true;
        break;
      }
      if (ch === ',' || ch === '\n') {
        needsQuotes = true;
      }
    }

    if (!needsQuotes) return cell;
    return hasDoubleQuote ? `"${cell.replace(/"/g, '""')}"` : `"${cell}"`;
  };

  const lines: string[] = [];
  if (headers.length > 0) {
    lines.push(headers.map(escapeCell).join(','));
  }
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(','));
  }
  return lines.join('\n');
};

/**
 * Escapes pipe and backslash characters for markdown table cells.
 */
const escapeMarkdownCell = (cell: string): string => {
  let needsEscape = false;
  for (const ch of cell) {
    if (ch === '\\' || ch === '|') {
      needsEscape = true;
      break;
    }
  }
  if (!needsEscape) return cell;

  const out: string[] = [];
  for (const ch of cell) {
    if (ch === '\\') out.push('\\\\');
    else if (ch === '|') out.push('\\|');
    else out.push(ch);
  }
  return out.join('');
};

/**
 * Formats table data as a markdown table string.
 */
export const toMarkdown = ({ headers, rows }: TableData): string => {
  if (headers.length === 0) return '';

  const lines: string[] = [];

  lines.push(`| ${headers.map(escapeMarkdownCell).join(' | ')} |`);
  lines.push(`| ${headers.map(() => '---').join(' | ')} |`);

  for (const row of rows) {
    const cells = headers.map((_, i) =>
      escapeMarkdownCell(i < row.length ? row[i] : ''),
    );
    lines.push(`| ${cells.join(' | ')} |`);
  }

  return lines.join('\n');
};

/**
 * Triggers a browser file download with the given content.
 */
export const triggerDownload = (
  content: string,
  filename: string,
  mimeType: string,
): void => {
  const blob = new Blob([content], { type: `${mimeType};charset=utf-8;` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
